import { useCallback, useMemo, useState } from "react";
import type { Task } from "@common/types/task";
import type {
    BoardColumnDef,
    BoardMove,
    BoardMoveCheck,
} from "@design-system/Board/Board.types";
import { useLatestRef } from "@design-system/hooks/useLatestRef";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import {
    needsTerminatingStatusConfirmation,
    useAutoMarkBlockedTaskAsDone,
} from "@hooks/useTerminatingStatusGuard";
import {
    createTaskMoveRule,
    getMovedTask,
    getTaskBoardMoveUpdate,
    type TaskBoardAxes,
} from "./taskBoardMoves";
import { canPlaceTaskBetween, getTaskBoardSortHint } from "./taskBoardPlace";
import { getTaskBoardSortLabel, type TaskBoardSort } from "./taskBoardSort";
import type { RestoreDroppedColumns } from "./useTaskBoardCollapse";
import {
    type TaskBoardSaveRequest,
    useTaskBoardSave,
} from "./useTaskBoardSave";
import { useTaskCompleter } from "./useTaskCompleter";

const DROP_KEYS = new Set([" ", "Enter"]);

/**
 * A keyboard drop lands on keydown, and the confirmation it opens focuses its
 * first control before the key comes up. Swallows that keyup so it can't tick
 * "Don't ask me again". Gives up after a second, for pointer drops.
 */
const swallowDropKeyUp = () => {
    const stop = () => {
        window.removeEventListener("keyup", swallow, true);
        window.clearTimeout(timeoutId);
    };
    const swallow = (event: KeyboardEvent) => {
        if (DROP_KEYS.has(event.key)) {
            event.preventDefault();
            event.stopPropagation();
        }
        stop();
    };
    window.addEventListener("keyup", swallow, true);
    const timeoutId = window.setTimeout(stop, 1000);
};

interface UseTaskBoardMoveArgs {
    tasks: readonly Task[];
    axes: TaskBoardAxes;
    /** The columns and lanes, to name a new assignee. */
    axisValues: readonly BoardColumnDef[];
    sort: TaskBoardSort;
    /** A drop landed in the cache; undone if its card goes back. */
    onMoveApplied: (columnIds: readonly string[]) => RestoreDroppedColumns;
}

/** A drop in the cache, and how to undo `onMoveApplied` if its card goes back. */
interface TaskBoardDrop {
    request: TaskBoardSaveRequest;
    restoreCollapse: () => void;
}

/**
 * Drag to move on the board. `dragProps` go on `Board`: the columns a card
 * can't land in, hold to place where the sort allows it, the task as a drop
 * leaves it (so a sorted drag previews its final spot), and `onMove`, which
 * writes the drop into the task's cached lists at once (`onMoveApplied`), so
 * the card stays where it landed, then saves it. A failed save puts the card
 * back with "Failed to move task". Finishing a blocked task asks first,
 * through the same confirmation List's status control shows; cancel puts the
 * card back. Either way back undoes `onMoveApplied`. Tasks are cached per
 * workspace, so this works on the organization page too.
 */
export const useTaskBoardMove = (args: UseTaskBoardMoveArgs) => {
    const { applyUpdate, save, revert } = useTaskBoardSave(
        "Failed to move task",
    );
    const { refuseIfViewOnly } = useViewOnlyRefusal();
    const { isAutoMarkSet, rememberAutoMark } = useAutoMarkBlockedTaskAsDone();
    const [pendingMove, setPendingMove] = useState<TaskBoardDrop | null>(null);
    const [shouldSkipConfirmation, setShouldSkipConfirmation] = useState(false);
    const completer = useTaskCompleter();
    const latestRef = useLatestRef({ ...args, completer, isAutoMarkSet });

    const saveMove = useCallback(
        ({ request, restoreCollapse }: TaskBoardDrop) =>
            save({ ...request, onRevert: restoreCollapse }),
        [save],
    );

    const onMove = useCallback(
        (move: BoardMove<Task>) => {
            if (refuseIfViewOnly()) {
                return;
            }
            const latest = latestRef.current;
            const update = getTaskBoardMoveUpdate({ ...latest, move });
            if (update === null) {
                return;
            }
            const task = move.item;
            const request = applyUpdate({ task, ...update });
            const { columnId } = move.to;
            const restoreCollapse = latest.onMoveApplied([columnId]);
            const drop = {
                request,
                restoreCollapse: () => restoreCollapse([columnId]),
            };
            const { status } = update.body;
            if (
                status !== undefined &&
                needsTerminatingStatusConfirmation({
                    task,
                    newStatus: status,
                    isAutoMarkSet: latest.isAutoMarkSet,
                })
            ) {
                swallowDropKeyUp();
                setPendingMove(drop);
                return;
            }
            saveMove(drop);
        },
        [refuseIfViewOnly, latestRef, applyUpdate, saveMove],
    );

    const closeConfirmation = () => {
        setPendingMove(null);
        setShouldSkipConfirmation(false);
    };

    const confirmMove = () => {
        if (pendingMove === null) {
            return;
        }
        if (shouldSkipConfirmation) {
            rememberAutoMark();
        }
        saveMove(pendingMove);
        closeConfirmation();
    };

    const cancelMove = () => {
        if (pendingMove !== null) {
            revert(pendingMove.request);
            pendingMove.restoreCollapse();
        }
        closeConfirmation();
    };

    const { tasks, axes, axisValues, sort } = args;
    const dragProps = useMemo(
        () => ({
            onMove,
            getMovedItem: <TTask extends Task>(move: BoardMoveCheck<TTask>) =>
                getMovedTask({
                    move,
                    tasks,
                    axes,
                    axisValues,
                    sort,
                    completer,
                }),
            canMove: createTaskMoveRule(axes),
            canPlaceBetween: canPlaceTaskBetween({ sort, axes }),
            sortHint: getTaskBoardSortHint({
                sort,
                axes,
                sortLabel: getTaskBoardSortLabel(sort),
            }),
        }),
        [onMove, tasks, axes, axisValues, sort, completer],
    );

    return {
        dragProps,
        /** Props for `TaskStatusTerminationConfirmationModal`. */
        confirmation: {
            pendingStatus: pendingMove?.request.body.status ?? null,
            activeBlockerCount:
                pendingMove?.request.task.numberOfActiveBlockedByTasks ?? 0,
            onConfirm: confirmMove,
            onCancel: cancelMove,
            showDontAskAgain: !isAutoMarkSet,
            shouldSkipConfirmation,
            onShouldSkipConfirmationChange: setShouldSkipConfirmation,
        },
    };
};
